import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, TrendingUp, Clock, Send, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function PredictiveAnalytics() {
  const [highRisk, setHighRisk] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [riskResponse, insightsResponse] = await Promise.all([
        api.get('/analytics/high-risk-appointments'),
        api.get('/analytics/pattern-insights')
      ]);
      setHighRisk(riskResponse.data);
      setInsights(insightsResponse.data);
    } catch (error) {
      toast.error('Failed to load predictive analytics');
    } finally {
      setLoading(false);
    }
  };

  const sendProactiveNotification = async (appointmentId) => {
    setSending({ ...sending, [appointmentId]: true });
    try {
      const response = await api.post(`/analytics/proactive-notify?appointment_id=${appointmentId}`);
      toast.success(`Sent ${response.data.customers_notified} proactive notifications`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setSending({ ...sending, [appointmentId]: false });
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getPatternIcon = (type) => {
    switch (type) {
      case 'day':
        return '📅';
      case 'time':
        return '⏰';
      case 'service':
        return '💼';
      default:
        return '📊';
    }
  };

  return (
    <Layout active="predictions">
      <div data-testid="predictions-page" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
            Predictive Analytics
          </h1>
          <p className="text-gray-600 mt-2">
            AI-powered insights to prevent revenue loss before it happens
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : (
          <>
            <Card className="bg-gradient-to-br from-teal-50 to-blue-50 border-teal-200 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-teal-100 p-3 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-teal-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Proactive Revenue Protection
                    </h3>
                    <p className="text-sm text-gray-700">
                      By analyzing historical patterns, we identify appointments likely to cancel 24-48 hours
                      in advance. Reach out to waitlist customers before the slot opens, improving fill rates
                      by 20-30% and maximizing recovered revenue.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="bg-white border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                    High-Risk Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-orange-600" style={{ fontFamily: 'Manrope' }}>
                    {highRisk.length}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Upcoming at-risk slots</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Lightbulb className="h-5 w-5 text-amber-600" />
                    Pattern Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-amber-600" style={{ fontFamily: 'Manrope' }}>
                    {insights.length}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Actionable patterns</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-border shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="h-5 w-5 text-teal-600" />
                    Lead Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-teal-600" style={{ fontFamily: 'Manrope' }}>
                    24-48h
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Early warning window</p>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                  High-Risk Appointments
                </h2>
                {highRisk.length === 0 ? (
                  <Card className="bg-white border-border shadow-sm">
                    <CardContent className="text-center py-12">
                      <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-2">No high-risk appointments detected</p>
                      <p className="text-sm text-gray-400">
                        System needs more historical data to identify patterns. Keep tracking cancellations!
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {highRisk.map((appt) => (
                      <Card
                        key={appt.appointment_id}
                        className="bg-white border-border shadow-sm"
                        data-testid={`high-risk-${appt.appointment_id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 space-y-3">
                              <div className="flex items-center gap-3">
                                <h3 className="text-lg font-semibold">{appt.service}</h3>
                                <Badge className={getRiskColor(appt.risk_level)}>
                                  {appt.risk_score}% Risk - {appt.risk_level.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-600">
                                <p>
                                  <span className="font-medium">Time:</span>{' '}
                                  {new Date(appt.start_time).toLocaleString()}
                                </p>
                                <p>
                                  <span className="font-medium">Staff:</span> {appt.staff_name}
                                </p>
                                <p>
                                  <span className="font-medium">Customer:</span> {appt.customer_name}
                                </p>
                              </div>
                              {Object.keys(appt.factors).length > 0 && (
                                <div className="bg-gray-50 p-3 rounded-md">
                                  <p className="text-xs font-medium text-gray-700 mb-2">Risk Factors:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(appt.factors).map(([factor, value]) => (
                                      <span
                                        key={factor}
                                        className="text-xs bg-white px-2 py-1 rounded border border-gray-200"
                                      >
                                        {factor.replace('_', ' ')}: {value}%
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <Button
                              onClick={() => sendProactiveNotification(appt.appointment_id)}
                              disabled={sending[appt.appointment_id]}
                              data-testid={`notify-${appt.appointment_id}`}
                              className="ml-4"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              {sending[appt.appointment_id] ? 'Sending...' : 'Alert Waitlist'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                  Pattern Insights
                </h2>
                {insights.length === 0 ? (
                  <Card className="bg-white border-border shadow-sm">
                    <CardContent className="text-center py-12">
                      <Lightbulb className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-2">No patterns identified yet</p>
                      <p className="text-sm text-gray-400">
                        Continue tracking cancellations to build your pattern database
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {insights.map((insight, idx) => (
                      <Card
                        key={idx}
                        className="bg-white border-border shadow-sm hover:shadow-md transition-all"
                        data-testid={`insight-${idx}`}
                      >
                        <CardContent className="p-6">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{getPatternIcon(insight.pattern_type)}</span>
                              <Badge variant="outline">{insight.cancellation_rate}% cancel rate</Badge>
                            </div>
                            <h3 className="font-semibold text-gray-900">{insight.description}</h3>
                            <p className="text-sm text-gray-600">{insight.recommendation}</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
